import React, { useState } from 'react';
import '../styles/register.reg.css';
export default function Register() {
  const [userType, setUserType] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  function normalizeRegistrationCode(value) {
    return (value || '').toString().trim().toUpperCase();
  }

  function isValidRegistrationCode(code) {
    return /^[A-Z0-9]{16}$/.test((code || '').toString().trim());
  }

  function toggleUserType(e) {
    setUserType(e.target.value);
  }

  function togglePassword(fieldId) {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.type = el.type === 'password' ? 'text' : 'password';
  }

  function showMessage(text, type, duration = 4000) {
    setMessage({ text, type });
    if (duration > 0) setTimeout(() => setMessage(null), duration);
  }

  function validatePasswords(password, confirm) {
    if (password !== confirm) {
      showMessage('Las contraseñas no coinciden', 'error');
      return false;
    }
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!regex.test(password)) {
      showMessage('La contraseña no cumple con los requisitos mínimos', 'error');
      return false;
    }
    return true;
  }

  // Submit registration to backend
  async function handleRegister(e) {
    e.preventDefault();
    const form = e.target;

    const password = form.password.value;
    const confirm = form.confirmPassword.value;
    if (!validatePasswords(password, confirm)) return;

    // Ensure user type selected
    if (!userType) {
      showMessage('Selecciona tipo de usuario', 'error');
      return;
    }

    // Build payload exactly as backend expects
    const firstName = (form.nombre.value || '').trim();
    const lastName = (form.apellido.value || '').trim();
    const name = `${firstName} ${lastName}`.trim();
    // always send personal RUT in `rut`; when `empresa`, also send `company_rut`
    const rut = (form.rutPersonal && form.rutPersonal.value) ? form.rutPersonal.value.trim() : '';
    const company_rut = (userType === 'empresa' && form.rutEmpresa && form.rutEmpresa.value)
      ? form.rutEmpresa.value.trim()
      : '';
    const email = (form.email && form.email.value) ? form.email.value.trim() : '';
    const passwordPlain = password; // do NOT hash on frontend

    // Código de creación: normalizar y validar formato antes de enviar
    const codigoRaw = (form.codigoCreacion && form.codigoCreacion.value) ? form.codigoCreacion.value : '';
    const codigo = normalizeRegistrationCode(codigoRaw);
    if (!codigo) {
      showMessage('Ingresa el código de creación proporcionado por Ker', 'error');
      return;
    }
    if (!isValidRegistrationCode(codigo)) {
      showMessage('Código inválido: debe tener 16 caracteres alfanuméricos.', 'error');
      return;
    }

    let company = '';
    let personal_address = '';
    let company_address = '';
    const is_company = userType === 'empresa' ? 1 : 0;

    let city = '';
    let region = '';
    if (userType === 'particular') {
      personal_address = (form.direccionParticular && form.direccionParticular.value) ? form.direccionParticular.value.trim() : '';
      // capture city and region for particular users
      city = (form.ciudadParticular && form.ciudadParticular.value) ? form.ciudadParticular.value.trim() : '';
      region = (form.regionParticular && form.regionParticular.value) ? form.regionParticular.value.trim() : '';
    } else if (userType === 'empresa') {
      company = (form.razonSocial && form.razonSocial.value) ? form.razonSocial.value.trim() : '';
      company_address = (form.direccionEmpresa && form.direccionEmpresa.value) ? form.direccionEmpresa.value.trim() : '';
      // allow admins to omit city/region for empresa; leave empty
      city = '';
      region = '';
    }

    const payload = {
      name,
      rut,
      company_rut,
      codigoCreacion: codigo,
      origen: 'formulario',
      city,
      region,
      email,
      password: passwordPlain,
      is_company,
      company,
      personal_address,
      company_address
    };


    setLoading(true);
    try {
      const base = process.env.REACT_APP_API_BASE_URL || '';
      const res = await fetch(`${base}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => ({}));
      setLoading(false);

      if (!res.ok) {
        const msg = data.message || data.error || (res.status === 400 ? 'Datos inválidos' : 'Error en el servidor');
        showMessage(msg, 'error');
        return;
      }

      // Registration successful. Backend handles hashing and email.
      // Show a persistent HTML success box informing the user an email was sent.
      showMessage(data.message || 'Usuario creado con éxito. Se ha enviado un correo con los detalles.', 'success', 0);
      // clear the form fields and reset UI state
      form.reset();
      setUserType('');
    } catch (err) {
      setLoading(false);
      showMessage('No se pudo conectar al servidor. Intenta nuevamente.', 'error');
      // console.error(err);
    }
  }

  function handleLoginRedirect(e) {
    e.preventDefault();
    // navigate to login without showing an alert
    window.location.href = '/login';
  }

  return (
    <div className="register-wrapper">
      <div className="register-container">
        <div className="logo-container">
           <a href="/" aria-label="Ir a inicio">
            <img src="/logo512.png" alt="Ker Logo" className="logo-icons" />
          </a>
        </div>

        <div className="register-header">
          <h1 className="register-title" id="form-title">Crear Cuenta</h1>
          <p className="register-subtitle" id="form-subtitle">Completa el formulario para registrarte</p>
        </div>

        <form id="register-form" onSubmit={handleRegister}>
          <div className="form-section">
            <div className="section-title">👤 Tipo de Usuario</div>
            <div className="user-type-selector">
              <label className="user-type-option">
                <input type="radio" name="userType" value="particular" onChange={toggleUserType} />
                <div className="user-type-label">
                  <span className="user-type-icon">🧑</span>
                  <span className="user-type-text">Usuario Particular</span>
                </div>
              </label>

              <label className="user-type-option">
                <input type="radio" name="userType" value="empresa" onChange={toggleUserType} />
                <div className="user-type-label">
                  <span className="user-type-icon">🏢</span>
                  <span className="user-type-text">Empresa</span>
                </div>
              </label>
            </div>
          </div>

          <div className="form-section">
            <div className="section-title">📋 Datos Personales</div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nombre">Nombre<span className="required">*</span></label>
                <div className="input-wrapper"><span className="input-icon">👤</span>
                  <input id="nombre" name="nombre" className="with-icon" placeholder="Tu nombre" required />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="apellido">Apellido<span className="required">*</span></label>
                <div className="input-wrapper"><span className="input-icon">👤</span>
                  <input id="apellido" name="apellido" className="with-icon" placeholder="Tu apellido" required />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="rut-personal">RUT Personal<span className="required">*</span></label>
              <div className="input-wrapper"><span className="input-icon">🆔</span>
                <input className="with-icon" id="rut-personal" name="rutPersonal" placeholder="12.345.678-9" required />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Correo Electrónico<span className="required">*</span></label>
              <div className="input-wrapper"><span className="input-icon">📧</span>
                <input id="email" name="email" type="email" className="with-icon" placeholder="correo@ejemplo.com" required />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="codigo-creacion">Código de Creación<span className="required">*</span></label>
              <div className="input-wrapper"><span className="input-icon">🔑</span>
                        <input id="codigo-creacion" name="codigoCreacion" className="with-icon" placeholder="Código proporcionado por Ker" required onBlur={(e) => { e.target.value = (e.target.value || '').toString().trim().toUpperCase(); }} />
              </div>
            </div>
          </div>

          {userType === 'particular' && (
            <div className="conditional-fields active" id="particular-fields">
              <div className="form-section">
                <div className="section-title">🏠 Dirección Personal</div>
                <div className="form-group"><label htmlFor="direccion-particular">Dirección<span className="required">*</span></label>
                  <div className="input-wrapper"><span className="input-icon">📍</span>
                    <input id="direccion-particular" name="direccionParticular" className="with-icon" placeholder="Calle, número, depto/casa" required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label htmlFor="ciudad-particular">Ciudad<span className="required">*</span></label>
                    <input id="ciudad-particular" name="ciudadParticular" placeholder="Ciudad" required />
                  </div>
                  <div className="form-group"><label htmlFor="region-particular">Región<span className="required">*</span></label>
                    <select id="region-particular" name="regionParticular" required>
                      <option value="">Selecciona región</option>
                      <option value="metropolitana">Región Metropolitana</option>
                      <option value="antofagasta">Antofagasta</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {userType === 'empresa' && (
            <div className="conditional-fields active" id="empresa-fields">
              <div className="form-section">
                <div className="section-title">🏢 Datos de la Empresa</div>
                <div className="form-group"><label htmlFor="razon-social">Razón Social<span className="required">*</span></label>
                  <div className="input-wrapper"><span className="input-icon">🏢</span>
                    <input id="razon-social" name="razonSocial" className="with-icon" placeholder="Nombre legal de la empresa" required />
                  </div>
                </div>
                <div className="form-group"><label htmlFor="rut-empresa">RUT Empresa<span className="required">*</span></label>
                  <div className="input-wrapper"><span className="input-icon">🆔</span>
                    <input id="rut-empresa" name="rutEmpresa" className="with-icon" placeholder="76.123.456-7" required />
                  </div>
                </div>
                <div className="form-group"><label htmlFor="direccion-empresa">Dirección de la empresa<span className="required">*</span></label>
                  <div className="input-wrapper"><span className="input-icon">🆔</span>
                    <input id="direccion-empresa" name="direccionEmpresa" className="with-icon" placeholder="Calle, número, depto/casa" required />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-section">
            <div className="section-title">🔒 Contraseña</div>
            <div className="form-group"><label htmlFor="password">Contraseña<span className="required">*</span></label>
              <div className="input-wrapper"><span className="input-icon">🔒</span>
                <input id="password" name="password" type="password" className="with-icon" placeholder="••••••••" required minLength={8} />
                <button type="button" className="password-toggle" onClick={() => togglePassword('password')} aria-label="Mostrar contraseña">👁️</button>
              </div>
            </div>
            <div className="form-group"><label htmlFor="confirm-password">Confirmar Contraseña<span className="required">*</span></label>
              <div className="input-wrapper"><span className="input-icon">🔒</span>
                <input id="confirm-password" name="confirmPassword" type="password" className="with-icon" placeholder="••••••••" required minLength={8} />
                <button type="button" className="password-toggle" onClick={() => togglePassword('confirm-password')} aria-label="Mostrar contraseña">👁️</button>
              </div>
            </div>
            <div className="password-requirements">
              <strong>La contraseña debe contener:</strong>
              <ul>
                <li>Mínimo 8 caracteres</li>
                <li>Al menos una letra mayúscula</li>
                <li>Al menos una letra minúscula</li>
                <li>Al menos un número</li>
              </ul>
            </div>
          </div>

          <button type="submit" className="register-button" id="register-btn" disabled={loading}>
            <span className="button-content">
              <span id="button-text">Registrarse</span>
              {loading && <div className="loading-spinner" id="loading-spinner" />}
            </span>
          </button>

          {message && <div id="message" className={`message ${message.type}`}>{message.text}</div>}
        </form>

        <div className="login-link" id="login-link">¿Ya tienes cuenta? <button type="button" className="login-cta" onClick={handleLoginRedirect}>Inicia sesión</button></div>
      </div>
    </div>
  );
}
